import React, { useEffect, useState, useRef } from "react";
import { Bell, Check } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { relativeTime } from "@/lib/utils-app";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (err) {
      console.warn("Failed to load notifications:", err?.message);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const unread = items.filter((i) => !i.read).length;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const markOne = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-sm text-zinc-700 hover:bg-zinc-100 transition"
      >
        <Bell className="w-5 h-5 stroke-[1.5]" />
        {unread > 0 && (
          <span
            data-testid="notification-unread-count"
            className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-blue-700 text-white text-[10px] font-semibold rounded-full flex items-center justify-center"
          >
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-zinc-200 rounded-sm z-50 animate-fade-in-up">
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
            <h4 className="text-sm font-semibold text-zinc-900">Notifikationer</h4>
            {unread > 0 && (
              <button
                data-testid="mark-all-read"
                onClick={markAll}
                className="text-xs text-blue-700 hover:underline flex items-center gap-1"
              >
                <Check className="w-3 h-3" /> Markera alla
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 && (
              <div className="px-4 py-8 text-sm text-zinc-500 text-center">Inga notifikationer</div>
            )}
            {items.map((n) => (
              <Link
                key={n.notif_id}
                to={n.case_id ? `/cases/${n.case_id}` : "#"}
                onClick={() => { markOne(n.notif_id); setOpen(false); }}
                data-testid={`notif-${n.notif_id}`}
                className={`block px-4 py-3 border-b border-zinc-100 hover:bg-zinc-50 ${!n.read ? "bg-blue-50/40" : ""}`}
              >
                <div className="text-sm font-medium text-zinc-900">{n.title}</div>
                <div className="text-xs text-zinc-600 mt-0.5 line-clamp-2">{n.body}</div>
                <div className="text-[10px] text-zinc-400 mt-1">{relativeTime(n.created_at)}</div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
